import type { RowStanding } from '@/features/microsoft/picker-chrome.js';
import type { OneDriveScope } from '@/lib/api/onedrive.js';

export type { RowStanding };

/**
 * What a granted folder is called on screen — the name its owner gave it, or OneDrive's own.
 *
 * The whole drive is the exception and has to be: Graph calls a drive's root `root`, so a sentence
 * built from the snapshot read "Already reachable through root."
 */
function scopeName(scope: OneDriveScope): string {
  if (scope.isDriveRoot) return 'your OneDrive';
  return scope.alias ?? scope.originalName;
}

/**
 * How a browsed folder stands against the folders already on the list.
 *
 * It mirrors identity's overlap rule, which is symmetric: a folder is refused when a granted folder
 * swallows it AND when it would swallow a granted one. The OneDrive picker only ever answered the
 * first half, so the Add beside a folder holding a granted child was a button that could only come
 * back as a conflict — the same defect the SharePoint picker had already fixed.
 *
 * `ancestors` is the browsed folder's own path, outermost first, which is what places the row: the
 * drive root and every folder opened to get here. A granted folder is inside this row when the row
 * is one of ITS ancestors, and covers this row when it is one of the row's.
 */
export function folderStanding(
  scopes: readonly OneDriveScope[],
  itemId: string,
  ancestors: readonly string[],
): RowStanding {
  const exact = scopes.find((scope) => scope.rootItemId === itemId);
  if (exact) return { kind: 'added' };

  const covering = scopes.find((scope) => ancestors.includes(scope.rootItemId));
  if (covering) return { kind: 'covered', by: scopeName(covering) };

  const inside = scopes.filter((scope) =>
    scope.rootAncestorItemIds.includes(itemId),
  );
  return inside.length > 0
    ? {
        kind: 'contains',
        blocking: inside.map((scope) => ({
          id: scope.scopeId,
          name: scopeName(scope),
        })),
      }
    : { kind: 'free' };
}
